import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Drawer, Loader } from '@mantine/core';
import { useTranslation, Trans } from 'react-i18next';
import { cartActions } from '../../reducers/storeCart';
import { billingAccountQuery } from '../../graphql-operations/BillingAccount.graphql';
import { priceCartForCheckout } from '../../lib/pricing';
import { executeStoreShopMutation } from '../../lib/purchase';
import { useLocale } from '../../contexts/LocaleContext';
import { centsToDollarsNumber, getLocalizedPricing } from '../../lib/util';
import { inAppShop } from '../../lib/analytics/events';
import { logInternalError } from '../../lib/errors';
import useProductSkuMap from '../../hooks/useProductSkuMap';
import { isBundleCartItem } from '../../lib/cart';
import AppPaths from '../../AppPaths';
import * as types from '../../types';
import { gqlTypes } from '../../types';
import { FREE_SHIPPING_CODE } from '../../types/Product';
import { isEnrolledInPositionDiscount } from '../../lib/positionDiscount';
import { ReactComponent as TrashIcon } from '../../assets/images/icons/trash.svg';
import { checkoutImageUrlForProduct, checkoutImageUrlForProductWithVariant } from '../CartItemList/CartItemList';
import styles from './InAppCheckoutSheet.module.scss';

export default function InAppCheckoutSheet() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { currency, currencyFormatter } = useLocale();
  const productSkuMap = useProductSkuMap();

  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const session = useSelector((state: types.AppState) => state.session);
  const opened = cart.showInAppCheckout === true;
  const cartItems = useMemo(() => Object.values(cart.cartItems), [cart.cartItems]);

  const [pricing, setPricing] = useState<types.CartPricing | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: accountData, loading: accountLoading } = useQuery<gqlTypes.billingAccount>(billingAccountQuery, {
    skip: !opened || !session,
    fetchPolicy: 'network-only',
  });

  const billingAccount = accountData?.currentUser?.billingAccount;
  const shippingAddress = billingAccount?.address;
  const paymentInfo = billingAccount?.billingInfo?.paymentInfo;
  const hasShipping = !!shippingAddress;
  const hasBilling = !!paymentInfo;

  useEffect(() => {
    if (!opened || cartItems.length === 0) return;

    setPricingLoading(true);
    setError(null);

    const address = shippingAddress ? { country: shippingAddress.country, postalCode: shippingAddress.zip } : undefined;

    // in-app purchases always qualify for free shipping — users are existing subscribers buying accessories
    priceCartForCheckout(cart, {
      checkoutType: types.CheckoutType.Store,
      shippingCode: FREE_SHIPPING_CODE,
      address,
    })
      .then(setPricing)
      .catch(() => setError(t('order_failed')))
      .finally(() => setPricingLoading(false));
  }, [opened, cartItems.length, shippingAddress, cart, t]);

  useEffect(() => {
    if (opened && cartItems.length === 0) {
      dispatch(cartActions.setShowInAppCheckout(false));
    }
  }, [opened, cartItems.length, dispatch]);

  // fire analytics on open
  useEffect(() => {
    if (opened) {
      try {
        inAppShop.checkoutOpened();
      } catch (err) {
        logInternalError(err);
      }
    }
  }, [opened]);

  const close = useCallback(() => {
    dispatch(cartActions.setShowInAppCheckout(false));
  }, [dispatch]);

  const removeItem = useCallback(
    (id: string) => {
      inAppShop.itemRemoved();
      dispatch(cartActions.removeCartItem({ id }));
    },
    [dispatch],
  );

  const confirmOrder = useCallback(async () => {
    if (!hasShipping || !hasBilling) return;

    setPurchasing(true);
    setError(null);
    inAppShop.orderConfirmed();

    try {
      // in-app users are pre-authenticated with billing on file — the backend
      // uses the stored payment method so we only need to pass shippingCode.
      // in-app purchases always qualify for free shipping (existing subscribers buying accessories).
      // 3DS is not supported in this flow; if required, the catch block
      // directs the user to the full checkout.
      await executeStoreShopMutation(cart, {
        shippingCode: FREE_SHIPPING_CODE,
      });
      inAppShop.orderCompleted();
      dispatch(cartActions.resetCart({ resetCoupon: true }));
      dispatch(cartActions.setShowInAppCheckout(false));
      history.push(AppPaths.CheckoutThankYou);
    } catch {
      inAppShop.orderFailed();
      setError(t('order_failed'));
    } finally {
      setPurchasing(false);
    }
  }, [cart, hasShipping, hasBilling, dispatch, history, t]);

  const goToFullCheckout = useCallback(() => {
    inAppShop.fullCheckoutTapped();
    close();
    history.push(AppPaths.CheckoutStripe);
  }, [close, history]);

  const formatCents = useCallback(
    (cents: number) => currencyFormatter.format(centsToDollarsNumber(cents)),
    [currencyFormatter],
  );

  const loading = accountLoading || pricingLoading;

  const paymentLabel = useMemo(() => {
    if (!paymentInfo) return null;
    if (paymentInfo.__typename === 'ObfuscatedCardInfo') {
      return {
        primary: `${t('card_ending_in')} ${paymentInfo.lastFour}`,
        secondary: `${t('expires')} ${paymentInfo.month}/${paymentInfo.year}`,
      };
    }
    if (paymentInfo.__typename === 'BankAccountInfo') {
      return {
        primary: `${paymentInfo.bankName} ····${paymentInfo.last4}`,
        secondary: paymentInfo.accountType,
      };
    }
    return null;
  }, [paymentInfo, t]);

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="bottom"
      size="auto"
      withCloseButton
      title={t('in_app_checkout_title')}
      closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
      styles={() => ({
        inner: {
          '.mantine-Drawer-content': {
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column' as const,
          },
        },
        title: {
          fontWeight: 700,
          fontSize: 20,
          width: '100%',
          textAlign: 'center' as const,
        },
        header: {
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)',
          justifyContent: 'center',
          flexShrink: 0,
        },
        body: {
          flex: 1,
          overflow: 'auto',
        },
      })}
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <Loader color="dark" />
          </div>
        ) : (
          <>
            {/* cart items */}
            {cartItems.map((item) => {
              const { product, variant } = productSkuMap.get(item.lineItem.sku) ?? {};
              if (!product) return null;

              const subProduct = isBundleCartItem(item)
                ? productSkuMap.get(item.subscriptionLineItem.sku)?.product
                : undefined;
              const subtitle = subProduct?.name ?? '';
              const priceInCents =
                getLocalizedPricing(product.pricingInCents, currency) +
                (subProduct ? getLocalizedPricing(subProduct.pricingInCents, currency) : 0);
              const imageUrl = variant
                ? checkoutImageUrlForProductWithVariant(product.id, variant)
                : checkoutImageUrlForProduct(product.id);

              return (
                <div key={item.cartItemId} className={styles.cartItem}>
                  <img src={imageUrl} alt={product.name} className={styles.cartItemImage} />
                  <div className={styles.cartItemInfo}>
                    <div className={styles.cartItemTitle}>{product.name}</div>
                    {subtitle && <div className={styles.cartItemSubtitle}>{subtitle}</div>}
                  </div>
                  <div className={styles.cartItemRight}>
                    <div className={styles.cartItemPrice}>{formatCents(priceInCents)}</div>
                    <button
                      type="button"
                      className={styles.removeButton}
                      onClick={() => removeItem(item.cartItemId)}
                      aria-label={t('remove')}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* shipping */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>{t('shipping')}</span>
                {hasShipping && (
                  <button type="button" className={styles.editLink} onClick={goToFullCheckout}>
                    {t('edit')}
                  </button>
                )}
              </div>
              {hasShipping ? (
                <div className={styles.sectionBody}>
                  {billingAccount?.firstName} {billingAccount?.lastName}
                  <br />
                  {shippingAddress.line1}
                  {shippingAddress.line2 && (
                    <>
                      <br />
                      {shippingAddress.line2}
                    </>
                  )}
                  <br />
                  {shippingAddress.city}
                  {', '}
                  {shippingAddress.state} {shippingAddress.zip}
                </div>
              ) : (
                <button type="button" className={styles.editLink} onClick={goToFullCheckout}>
                  {t('add_shipping_address')}
                </button>
              )}
            </div>

            {/* billing */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>{t('billing')}</span>
                {hasBilling && (
                  <button type="button" className={styles.editLink} onClick={goToFullCheckout}>
                    {t('edit')}
                  </button>
                )}
              </div>
              {paymentLabel ? (
                <div className={styles.sectionBody}>
                  {paymentLabel.primary}
                  <br />
                  {paymentLabel.secondary}
                </div>
              ) : (
                <button type="button" className={styles.editLink} onClick={goToFullCheckout}>
                  {t('add_payment_method')}
                </button>
              )}
            </div>

            {/* promo banner */}
            {(cart.couponCode || (pricing && pricing.appliedDiscountInCents > 0)) && (
              <div className={styles.promoSection}>
                <div className={styles.promoBanner}>
                  <span className={styles.promoLabel}>{t('promo_applied_exclamation')}</span>
                  <span className={styles.promoDescription}>
                    {pricing?.couponDiscount?.name ?? t('multi_pet_discount_description')}
                  </span>
                </div>
              </div>
            )}

            {/* order summary */}
            {pricing && (
              <div
                className={
                  cart.couponCode || pricing.appliedDiscountInCents > 0 ? styles.sectionNoBorder : styles.section
                }
              >
                <div className={styles.summaryRow}>
                  <span>{t('subtotal')}</span>
                  <span>{formatCents(pricing.subtotalInCents)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>{t('shipping')}</span>
                  <span>{pricing.shippingInCents ? formatCents(pricing.shippingInCents) : t('free')}</span>
                </div>
                {pricing.appliedDiscountInCents > 0 && (
                  <div className={`${styles.summaryRow} ${styles.summaryDiscount}`}>
                    <span>{isEnrolledInPositionDiscount(pricing) ? t('multi_pet_discount') : t('discount')}</span>
                    <span>{`-${formatCents(pricing.appliedDiscountInCents)}`}</span>
                  </div>
                )}
                {pricing.taxInCents != null && pricing.taxInCents > 0 && (
                  <div className={styles.summaryRow}>
                    <span>{t('taxes')}</span>
                    <span>{formatCents(pricing.taxInCents)}</span>
                  </div>
                )}
                <div className={styles.totalRow}>
                  <span>{t('total')}</span>
                  <span>{formatCents(pricing.totalInCents)}</span>
                </div>
              </div>
            )}

            {/* error */}
            {error && (
              <>
                <div className={styles.error}>{error}</div>
                <button type="button" className={styles.fallbackLink} onClick={goToFullCheckout}>
                  {t('try_full_checkout')}
                </button>
              </>
            )}

            {/* actions */}
            <button
              type="button"
              className={styles.confirmButton}
              onClick={() => void confirmOrder()}
              disabled={purchasing || !hasShipping || !hasBilling}
            >
              {purchasing ? <Loader color="white" size="sm" /> : t('confirm_order')}
            </button>
            <button
              type="button"
              className={styles.continueShoppingButton}
              onClick={() => {
                inAppShop.continueShopping();
                close();
                history.push(AppPaths.ProductListingPage);
              }}
            >
              {t('continue_shopping')}
            </button>
            <div className={styles.terms}>
              <Trans
                i18nKey="in_app_checkout_terms"
                components={{
                  termsLink: (
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a href="https://fitracking.com/tos" target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              />
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
}
